import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { type PropsWithChildren, useCallback, useMemo } from 'react'
import { fetchCurrentUser, login, logout } from '../api/authClient'
import type { LoginInput } from '../model/contracts'
import { AuthContext, type AuthContextValue } from './authContext'

const AUTH_QUERY_KEY = ['auth', 'me']

export function AuthProvider({ children }: PropsWithChildren) {
  const queryClient = useQueryClient()

  const sessionQuery = useQuery({
    queryKey: AUTH_QUERY_KEY,
    queryFn: ({ signal }) => fetchCurrentUser(signal),
    retry: false,
  })

  const loginMutation = useMutation({
    mutationFn: (input: LoginInput) => login(input),
    onSuccess: (user) => {
      queryClient.setQueryData(AUTH_QUERY_KEY, user)
    },
  })

  const logoutMutation = useMutation({
    mutationFn: () => logout(),
    onSettled: () => {
      queryClient.setQueryData(AUTH_QUERY_KEY, null)
    },
  })

  const loginWithPassword = useCallback(
    async (input: LoginInput) => {
      return loginMutation.mutateAsync(input)
    },
    [loginMutation],
  )

  const logoutUser = useCallback(async () => {
    await logoutMutation.mutateAsync()
  }, [logoutMutation])

  const value = useMemo<AuthContextValue>(
    () => ({
      user: sessionQuery.data ?? null,
      isLoading: sessionQuery.isLoading,
      isAuthenticated: Boolean(sessionQuery.data),
      isLoginPending: loginMutation.isPending,
      isLogoutPending: logoutMutation.isPending,
      loginWithPassword,
      logoutUser,
    }),
    [
      loginMutation.isPending,
      loginWithPassword,
      logoutMutation.isPending,
      logoutUser,
      sessionQuery.data,
      sessionQuery.isLoading,
    ],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
